import { PortfolioSubstitutionAlerts } from '@ddt-web/domain';
import { HoldingsUtil } from '@ddt-web/generic-utils';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';

import { AlertConstants } from '../alert/alert.constants';
import { EntityFlags } from './entity-flags';
import { PartnerPortfolioFlagUtils } from './partner-portfolio-flag-utils';

export class AlertUtil {

	/**
	 * Get alert messages for portfolio
	 * - has invalid security substitutions alerts - cash replacements
	 * - has asset class substitution alerts
	 * - has portfolio altered/processed
	 *
	 * @static
	 * @param {PortfolioSubstitutionAlerts} portfolioSubstitutionAlert
	 * @param {EntityFlags} portfolioFlags
	 * @returns {Array<string>}
	 * @memberof AlertUtil
	 */
	public static getPortfolioAlertMessages(portfolioSubstitutionAlert: PortfolioSubstitutionAlerts, portfolioFlags: EntityFlags): Array<string> {
		const alertsInfo: string[] = [];

		//handling other cases where invalid securities are replaced by holdings other than cash
		if (!isEmpty(portfolioSubstitutionAlert)) {
			const hasInvalidSecurityAlerts = AlertUtil.hasInvalidSecuritySubstitutionAlerts(portfolioSubstitutionAlert);
			if (hasInvalidSecurityAlerts) {
				const alertIdentifierKeys = [...portfolioSubstitutionAlert.invalidSecuritiesAlertsMsgMap.keys()];
				const hasCashAlerts = alertIdentifierKeys.some(key => HoldingsUtil.isCashIdentifier(key));
				const hasInvalidSecurityAlerts = alertIdentifierKeys.some(key => !HoldingsUtil.isCashIdentifier(key));

				if (hasCashAlerts) {
					alertsInfo.push(AlertConstants.ALERT_CASH_SUBSTITUTIONS_INFO);
				}
				if (hasInvalidSecurityAlerts) {
					alertsInfo.push(AlertConstants.ALERT_INVALID_SECURITY_SUBSTITUTIONS_INFO);
				}
			}

			if (AlertUtil.hasPortfolioAssetSubstitutionAlerts(portfolioSubstitutionAlert)) {
				alertsInfo.push(AlertConstants.ALERT_ASSET_CLASS_OR_BOND_SUBSTITUTIONS_INFO);
			}
		}

		if (!isEmpty(portfolioFlags)) {
			if (PartnerPortfolioFlagUtils.isModelDirty(portfolioFlags)) {
				alertsInfo.push(AlertConstants.PORTFOLIO_ALTERED_ALERT_TEXT);
			}
		}

		return alertsInfo;
	}

	/**
	 * Get alert messages for multiple portfolio
	 *
	 * @static
	 * @param {Array<PortfolioSubstitutionAlerts>} portfolioSubstitutionAlerts
	 * @param {Array<EntityFlags>} portfolioFlags
	 * @returns {any[]}
	 * @memberof AlertUtil
	 */
	public static getMultiplePortfolioAlertMessages(portfolioSubstitutionAlerts: Array<PortfolioSubstitutionAlerts>, portfolioFlags: Array<EntityFlags>): any[] {
		const alertsMessages: any[] = [];
		portfolioSubstitutionAlerts.forEach((alertMap, index) => {
			alertsMessages.push(AlertUtil.getPortfolioAlertMessages(alertMap, portfolioFlags[index]));
		});
		return alertsMessages;
	}


	/**
	 * For multiple portfolios
	 * Returns true if any of the portfolios has asset substitution alert
	 * To show asset class substitution disclosure
	 *
	 * @static
	 * @param {Array<PortfolioSubstitutionAlerts>} portfolioSubstitutionAlerts
	 * @returns {boolean}
	 * @memberof AlertUtil
	 */
	public static hasMultiplePortfolioAssetSubstitutionAlerts(portfolioSubstitutionAlerts: Array<PortfolioSubstitutionAlerts>): boolean {
		return portfolioSubstitutionAlerts.some(portfolioSubstitutionAlert => !isEmpty(get(portfolioSubstitutionAlert, 'assetClassAlertsMsgMap')));
	}

	/**
	 * Returns true if a portfolio has asset substitution alert
	 *
	 * @static
	 * @param {PortfolioSubstitutionAlerts} portfolioSubstitutionAlert
	 * @returns {boolean}
	 * @memberof AlertUtil
	 */
	public static hasPortfolioAssetSubstitutionAlerts(portfolioSubstitutionAlert: PortfolioSubstitutionAlerts): boolean {
		return !isEmpty(get(portfolioSubstitutionAlert, 'assetClassAlertsMsgMap'));
	}

	/**
	 * Returns true if a portfolio has invalid security alert
	 *
	 * @static
	 * @param {PortfolioSubstitutionAlerts} portfolioSubstitutionAlert
	 * @returns {boolean}
	 * @memberof AlertUtil
	 */
	public static hasInvalidSecuritySubstitutionAlerts(portfolioSubstitutionAlert: PortfolioSubstitutionAlerts): boolean {
		return !isEmpty(get(portfolioSubstitutionAlert, 'invalidSecuritiesAlertsMsgMap'));
	}

}
